// Roster of one group: search, remove a student (confirmed), or move them to
// another group of the same niveau/type. Removal keeps the link as history.
import React, { useState, useEffect } from 'react';
import { X, Search, Loader2, UserMinus, ArrowRightLeft, Users } from 'lucide-react';
import type { GroupeWithCounts, StudentBasic, TypeFormation } from '@/types/group.types';
import { getNiveauLabel, TypeFormationLabels } from '@/types/group.types';
import {
  getStudentsInGroup,
  removeStudentFromGroup,
  reassignStudent,
} from '@/services/groupsService';
import CuteConfirmModal from '@/shared/components/CuteConfirmModal';

interface Props {
  group: GroupeWithCounts;
  allGroups: GroupeWithCounts[]; // All groups for same niveau/type for reassignment
  /** False = read-only roster (no remove / reassign actions). */
  canManage?: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const ViewGroupStudentsModal: React.FC<Props> = ({
  group,
  allGroups,
  canManage = true,
  onClose,
  onSuccess,
  onError,
}) => {
  const [students, setStudents] = useState<StudentBasic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<StudentBasic | null>(null);
  const [reassignModal, setReassignModal] = useState<{
    student: StudentBasic;
    targetGroupId: string;
  } | null>(null);

  // Load students in this group
  useEffect(() => {
    const loadStudents = async () => {
      setIsLoading(true);
      try {
        const data = await getStudentsInGroup(group.id);
        setStudents(data);
      } catch (err) {
        onError(err instanceof Error ? err.message : 'Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };

    loadStudents();
    // onError is a reporting callback, not a data dependency.
  }, [group.id]);

  // Filter students by search
  const filteredStudents = students.filter((s) => {
    const query = searchQuery.toLowerCase();
    return (
      s.firstName.toLowerCase().includes(query) ||
      s.lastName.toLowerCase().includes(query) ||
      s.studentCode.toLowerCase().includes(query)
    );
  });

  // Remove the student from THIS group only (confirmed first)
  const handleRemoveStudent = async (): Promise<void> => {
    if (!removeTarget) return;
    const studentId = removeTarget.id;
    setIsProcessing(studentId);
    try {
      await removeStudentFromGroup(studentId, group.id);
      setStudents((prev) => prev.filter((s) => s.id !== studentId));
      setRemoveTarget(null);
      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors du retrait');
    } finally {
      setIsProcessing(null);
    }
  };

  // Handle reassign student
  const handleReassign = async () => {
    if (!reassignModal) return;

    setIsProcessing(reassignModal.student.id);
    try {
      await reassignStudent(reassignModal.student.id, reassignModal.targetGroupId);
      setStudents((prev) => prev.filter((s) => s.id !== reassignModal.student.id));
      setReassignModal(null);
      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de la réassignation');
    } finally {
      setIsProcessing(null);
    }
  };

  // Get other groups for reassignment (same niveau/type, different group)
  const otherGroups = allGroups.filter((g) => g.id !== group.id);

  const isFC = group.typeFormation === 'CONTINUE';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{group.name}</h3>
            <p className="text-sm text-gray-500">
              {getNiveauLabel(group.niveau)} • {TypeFormationLabels[group.typeFormation as TypeFormation]}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Stats Bar */}
        <div className={`px-4 py-2 flex items-center gap-4 text-sm ${isFC ? 'bg-purple-50' : 'bg-primary-50'}`}>
          <div className={`flex items-center gap-2 ${isFC ? 'text-purple-700' : 'text-primary-700'}`}>
            <Users className="h-4 w-4" />
            <span>
              {students.length} étudiant(s)
              {group.maxStudents && ` / ${group.maxStudents} max`}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Aucun étudiant dans ce groupe
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un étudiant..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Student List */}
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {filteredStudents.map((student) => (
                  <div
                    key={student.id}
                    className="flex items-center justify-between px-3 py-2 hover:bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {student.lastName} {student.firstName}
                      </p>
                      <p className="text-xs text-gray-500">{student.studentCode}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {canManage && otherGroups.length > 0 && (
                        <button
                          onClick={() =>
                            setReassignModal({
                              student,
                              targetGroupId: otherGroups[0].id,
                            })
                          }
                          disabled={isProcessing === student.id}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                          title="Réassigner à un autre groupe"
                        >
                          <ArrowRightLeft className="h-4 w-4" />
                        </button>
                      )}
                      {canManage && (
                        <button
                          onClick={() => setRemoveTarget(student)}
                          disabled={isProcessing === student.id}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                          title="Retirer du groupe"
                        >
                          {isProcessing === student.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <UserMinus className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {filteredStudents.length === 0 && searchQuery && (
                <p className="text-center text-sm text-gray-500 py-4">
                  Aucun résultat pour "{searchQuery}"
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>

      {/* Remove confirmation — the link is kept as history, only closed */}
      <CuteConfirmModal
        isOpen={removeTarget !== null}
        title="Retirer du groupe"
        itemName={removeTarget ? `${removeTarget.lastName} ${removeTarget.firstName}` : undefined}
        message={`L'étudiant quittera le groupe "${group.name}" et repassera en attente d'affectation. Son historique (notes, présences) dans ce groupe est conservé.`}
        variant="warning"
        confirmLabel="Retirer"
        cancelLabel="Annuler"
        isLoading={removeTarget !== null && isProcessing === removeTarget.id}
        onConfirm={handleRemoveStudent}
        onCancel={() => setRemoveTarget(null)}
      />

      {/* Reassign Modal */}
      {reassignModal && (
        <div className="fixed inset-0 z-60 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setReassignModal(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              Réassigner l'étudiant
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              Réassigner{' '}
              <strong>
                {reassignModal.student.lastName} {reassignModal.student.firstName}
              </strong>{' '}
              vers:
            </p>
            <select
              value={reassignModal.targetGroupId}
              onChange={(e) =>
                setReassignModal((prev) =>
                  prev ? { ...prev, targetGroupId: e.target.value } : null
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
            >
              {otherGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} ({g.studentCount}
                  {g.maxStudents && `/${g.maxStudents}`} étudiants)
                </option>
              ))}
            </select>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setReassignModal(null)}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleReassign}
                disabled={isProcessing === reassignModal.student.id}
                className="px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isProcessing === reassignModal.student.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Réassigner'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewGroupStudentsModal;
